/**
 * Splits a path into segments, filtering any empty/bad nodes.
 * This is useful for converting file paths into tree segments.
 *
 * @param {string} path - A full file path (e.g., '/src/components/Button.tsx')
 * @returns {string[]} - Array of path segments (e.g., ['src', 'components', 'Button.tsx'])
 *
 * @example
 * splitPathIntoSegments('/src/components/Button.tsx')
 * // => ['src', 'components', 'Button.tsx']
 *
 * splitPathIntoSegments('src/components/')
 * // => ['src', 'components']
 */
export const splitPathIntoSegments = (path: string): string[] => path
  .split('/')
  .filter(segment => segment?.trim());

/**
 * Returns the full file path for a given node by combining it with its parent path.
 * Handles special cases for root paths and empty parents.
 *
 * @param {string} nodePath - The path of this node segment (e.g., 'components')
 * @param {string} parentPath - The node's parent path segment (e.g., '/src')
 * @returns {string} - The full path (e.g., '/src/components')
 *
 * @example
 * getFullPath('src', '')
 * // => 'src'
 *
 * getFullPath('components', '/')
 * // => '/components'
 *
 * getFullPath('Button.tsx', '/src/components')
 * // => '/src/components/Button.tsx'
 */
export function getFullPath(nodePath: string, parentPath = ''): string {
  if (parentPath === '') {
    return nodePath;
  }
  if (parentPath === '/') {
    return `/${nodePath}`;
  }
  return `${parentPath}/${nodePath}`;
}
